import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { CSSTransition } from 'react-transition-group';
import Portal from '../../utils/Portal';
import { useAppStore } from '../../stores';

import DetailHeader from '../Detail/DetailHeader';
import ModalCloseBtn from './ModalCloseBtn';

import './Modal.scss';

const Modal = (props) => {
  const [scrollClassActive, setScrollClassActive] = useState(false);
  const [hasContentBelow, setHasContentBelow] = useState(false);
  const modalRef = useRef(null);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);
  const settings = useAppStore((storeState) => storeState.settings);
  const isYourTour = settings?.platform === 'yt';

  const {
    isOpen,
    modalSize,
    modalStyle, // For positioned modals.
    modalClassName,
    showCloseButton,
    showBlur,
    onEnter,
    handleModalHide,
    onExitFinish,
    title,
    headerData,
    navBtns,
    modalRootId,
    isLoading,
  } = props;

  const handleScroll = (event) => {
    const scroll = event.currentTarget.scrollTop;
    if (scroll > 20) {
      setScrollClassActive(true);
    } else if (
      scroll < 4) {
      setScrollClassActive(false);
    }
  };

  // If the modal was previously open, the scrollClassActive state may be true.
  useEffect(() => {
    setScrollClassActive(false);
  }, [props.children, isOpen]);

  // Use IntersectionObserver on a sentinel at the bottom of modal__content.
  // When the sentinel is not visible inside the scrollable .modal, there's
  // content below the fold.
  const sentinelCallbackRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    sentinelRef.current = node;
    if (!node) return;

    const modal = node.closest('.modal');
    if (!modal) return;
    modalRef.current = modal;

    const observer = new IntersectionObserver(
      ([entry]) => setHasContentBelow(!entry.isIntersecting),
      { root: modal, threshold: 0.1 },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);


  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modalOpen');
    } else {
      document.body.classList.remove('modalOpen');
    }
    // Cleanup in case the component unmounts while modal is open
    return () => {
      document.body.classList.remove('modalOpen');
    };
  }, [isOpen]);

  return (
    <Portal portalId={modalRootId}>
      <CSSTransition
        in={isOpen}
        timeout={500}
        classNames="modalContainer-"
        appear
        unmountOnExit
        onExit={handleModalHide}
        onEnter={onEnter}
        onExited={() => {
          if (onExitFinish) {
            onExitFinish();
          }
        }}
      >
        <div className="modalContainer">

          {showBlur && (
            /* eslint-disable jsx-a11y/click-events-have-key-events */
            /* eslint-disable jsx-a11y/no-static-element-interactions */
            /* This is an actual element instead of a pseudo element because it
            allows the blur to be clicked to close the modal. */
            <div
              className="modalBlur"
              onClick={handleModalHide}
            />
          )}

          <div
            className={classNames(`modal ${modalClassName || ''}`, {
              'modal--floating modal--small ': modalSize === 'small',
              'modal--small-medium ': modalSize === 'small-medium',
              'modal--floating modal--medium': modalSize === 'medium',
              'modal--noHeader modal--small-medium ': modalSize === 'noheader-small-medium',
              'modal--scrollActive': scrollClassActive,
              'modal--loading': isLoading,
            })}
            onScroll={handleScroll}
            style={modalStyle}
          >

            <div className={classNames('modalHeader', {
              'modalHeader--anchored': modalClassName === 'modal--anchored',
            })}
            >
              <div className="modalHeader__outer">
                <DetailHeader
                  headerClassName="modalHeader__inner"
                  title={title}
                  data={headerData}
                  navClassName="modalHeader__nav"
                  navBtns={navBtns}
                />
              </div>

              {showCloseButton && (
                <ModalCloseBtn closeFunc={handleModalHide} />
              )}
            </div>

            <div className="modal__content">
              {props.children}
            </div>

            <div ref={sentinelCallbackRef} className="modal__sentinel" />

            {!isYourTour && scrollClassActive && (
              <div className="modal__backToTop">
                <button
                  type="button"
                  className="modal__backToTop__button"
                  onClick={() => {
                    modalRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <span className="modal__backToTop__button__arrow" />
                  <span className="modal__backToTop__button__text">Back to Top</span>
                </button>
              </div>
            )}

            <CSSTransition
              in={!isYourTour && hasContentBelow}
              timeout={300}
              classNames="modal__scrollOverlay-"
              unmountOnExit
            >
              <div className="modal__scrollOverlay">
                <div className="modal__scrollOverlay__indicator">
                  <span className="modal__scrollOverlay__indicator__text">Scroll for More</span>
                  <span className="modal__scrollOverlay__indicator__arrow" />
                </div>
              </div>
            </CSSTransition>

          </div>
        </div>
      </CSSTransition>
    </Portal>
  );
};


Modal.propTypes = {
  title: PropTypes.string,
  headerData: PropTypes.object,
  navBtns: PropTypes.array,
  showCloseButton: PropTypes.bool,
  handleModalHide: PropTypes.func,
  modalClassName: PropTypes.string,
  modalSize: PropTypes.oneOf([
    'small',
    'small-medium',
    'medium',
    'noheader-small-medium',
  ]),
  scrollClassActive: PropTypes.bool,
  modalStyle: PropTypes.object,
  props: PropTypes.object,
  children: PropTypes.node,
  modalRootId: PropTypes.string,
  isLoading: PropTypes.bool,
};

Modal.defaultProps = {
  // title: intentionally omitted - will be undefined unless explicitly passed. Only show skeleton when explicitly set to null
  headerData: null,
  // navBtns: intentionally omitted - will be undefined unless explicitly passed. Only show skeleton when explicitly set to null
  showCloseButton: false,
  handleModalHide: () => { },
  modalClassName: null,
  modalSize: null,
  scrollClassActive: false,
  modalStyle: {},
  props: {},
  children: null,
  modalRootId: 'modalRoot',
  isLoading: false,
};

export default Modal;
